import * as _ from 'underscore'

//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { Enums } from "../../models/common"
import { ColumnModel, GridRequestModel } from "../../modules/api/api.models"
import { ConvertHelper, LocalizationHelper, ToastHelper, LogHelper, CommonHelper, TypeHelper, DataType, DataTypeGroupComponent } from "../../helpers"
import { USER_SESSION_SERVICE_NAME, UserSessionService } from "../user-session"
import { OpDataTypeGroup, OpView, OpViewColumn, OpDataTypeInGroup, OpProfileData } from "../../models/core"
import { ColumnInfo } from '../../models/custom'
import { DataProviderListResult, DataProviderRequest } from '../../data-provider'
import { GridService, GRID_SERVICE_NAME } from '../grid'
import { GetViewColumnOptions } from '../../models/core/op-view'
import { $STATE_SERVICE_NAME, DetailsOptions, DetailsResult, SystemService, SYSTEM_SERVICE_NAME } from '../system'
import { CreateEditInitParams } from '../../modules/common/create-edit-popup'

//#region Public Types

export const DATA_TYPE_GROUP_SERVICE_NAME = 'dataTypeGroupService'
export const DATA_TYPE_GROUP_SERVICE_PROVIDER_NAME = 'dataTypeGroupServiceProvider'

//#endregion

export class DataTypeGroupService {

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Common.DataTypeGroup]' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdDataTypeGroup };
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Datatype_Group }
    public static get FILE_NAME_PREFIX(): string { return 'dataTypeGroup' };

    public static get ID_FIELD() { return 'id' }
    public static get PARENT_ID_FIELD(): string { return 'parentId' }

    public get idDataTypeColumnName(): string { return 'id_data_type' }
    public get nameDataTypeColumnName(): string { return 'data_type_name' }
    public get descrDataTypeColumnName(): string { return 'data_type_descr' }
    public get orderNbrColumnName(): string { return 'order_nbr' }
    public get customDescrColumnName(): string { return 'custom_descr' }
    public get customDescrObject(): string { return 'custom_descr_object' }

    private pageSize: number = 10000

    // #region Ctor

    constructor(
        private userSession: UserSessionService,
        private gridService: GridService,
        private $state: StateService,
        private readonly systemService: SystemService
    ) { }

    // #endregion    

    // #region Public Methods

    public getDataTypeGroupFilter(columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string): JQueryPromise<DataProviderListResult<OpDataTypeGroup>> {
        const allowedDataTypeGroups: OpProfileData[] =
            (this.userSession.getLoggedOperator().profile != null &&
                this.userSession.getLoggedOperator().profile.parameters != null)
                ? _.filter(this.userSession.getLoggedOperator().profile.parameters.toArray(), (data: OpProfileData) => data.idDataType == Enums.DataType.PROFILE_ALLOWED_DATA_TYPE_GROUP)
                : null

        if (_.size(allowedDataTypeGroups) > 0) {
            return this.userSession
                .getDataProvider()
                .View
                .getActiveView(DataTypeGroupService.REFERENCE_TYPE)
                .then((view: OpView) => {
                    const nameColumn: OpViewColumn = view.getViewColumn(new GetViewColumnOptions(Enums.DataType.HELPER_NAME))

                    if (nameColumn != null) {
                        let isClauseEmpty: boolean = true

                        if (customWhereClause == null) {
                            customWhereClause = TypeHelper.emptyString
                        } else if (customWhereClause.length > 0) {
                            isClauseEmpty = false
                            customWhereClause += ' AND ('
                        }


                        _.forEach(allowedDataTypeGroups, (data: OpProfileData) => {
                            if (customWhereClause.length > 0) {
                                customWhereClause += ' OR '
                            }

                            customWhereClause += `[${nameColumn.name}] = '${data.value}'`
                        })

                        if (isClauseEmpty == false) {
                            customWhereClause += ')'
                        }
                    }

                    return this.userSession.getDataProvider().DataTypeGroup.getDataTypeGroupFilter(columns, pageNbr, pageSize, customWhereClause)
                })
        } else {
            return this.userSession.getDataProvider().DataTypeGroup.getDataTypeGroupFilter(columns, pageNbr, pageSize, customWhereClause)
        }
    }

    public saveDataTypeGroup(toSave: OpDataTypeGroup) {
        const deferred: JQueryDeferred<OpDataTypeGroup> = $.Deferred<OpDataTypeGroup>()

        this.userSession
            .getDataProvider()
            .DataTypeGroup
            .saveDataTypeGroup(toSave)
            .then((savedObject: OpDataTypeGroup) => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, DataTypeGroupService.SIMAX_MODULE_NAME))
                CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.deviceSaved, true, 0)

                deferred.resolve(savedObject)
            })
            .fail((error: any) => {
                deferred.reject(error)
            })

        return deferred.promise()
    }

    public getMainGridColumns(): JQueryPromise<ColumnModel[]> {
        return this.gridService
            .getColumnModelByCategory(
                [
                    Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
                    Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
                ],
                DataTypeGroupService.SIMAX_MODULE_NAME,
                {
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSession.getLoggedOperator().idDistributor
                })
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                let defColExist: boolean = false

                // Check if in defaultColumns are columns which are not in columnsDictionary
                defaultColumns.forEach((defCol: ColumnModel) => {
                    if (defCol.type === Enums.DataTypeClass.Datetime) {
                        defCol.type = Enums.DataTypeClass.Date
                    }

                    defColExist = false
                    columns.forEach((col: ColumnModel) => {
                        if (col.type === Enums.DataTypeClass.Datetime) {
                            col.type = Enums.DataTypeClass.Date
                        }

                        if (defCol.id == col.id) {
                            defColExist = true
                        }
                    })

                    if (defColExist == false) {
                        columns.push(defCol)
                    }
                })

                return $.when(columns, defaultColumns)
            })
    }

    public getDataTypeGroups(columns: ColumnModel[]): JQueryPromise<DataProviderListResult<any>> {
        const request: GridRequestModel = new GridRequestModel();
        request.columns = columns
        request.pageNumber = 1
        request.pageSize = this.pageSize

        return this.userSession
            .getDataProvider()
            .View
            .getActiveView(DataTypeGroupService.REFERENCE_TYPE)
            .then((view: OpView) => {
                request.where = this.prepareCustomWhereClause(view, request.columns)

                return this.getDataTypeGroupFilter(request.columns.map((value: ColumnModel) => new ColumnInfo(value)), request.pageNumber, request.pageSize, request.where)
                    .then((response: DataProviderListResult<OpDataTypeGroup>) => {
                        const result: DataProviderListResult<OpDataTypeGroup> = <DataProviderListResult<OpDataTypeGroup>>{
                            error: response.error,
                            totalCount: response.totalCount,
                            objects: []
                        }

                        if (response != null && _.size(response.objects) > 0) {
                            if (response.totalCount > this.pageSize) {
                                ToastHelper.showInfo(LocalizationHelper.getLocalizedText('FoundTooManyRecords', { collection: 'Grid' })
                                    .replace('{0}', response.totalCount)
                                    .replace('{1}', this.pageSize), { displayTime: 4000 })
                            }

                            result.objects = this.prepareGridObjects(response.objects)
                        }

                        return result
                    })
            })
    }

    public getDataTypesInGroup(idDataTypeGroup: number): JQueryPromise<OpDataTypeInGroup[]> {
        return this.userSession
            .getDataProvider()
            .DataTypeGroup
            .getDataTypesInGroup(idDataTypeGroup)
    }

    //#region Delete

    public deleteDataTypeGroup(idDataTypeGroup: number): JQueryPromise<void> {
        return this.userSession
            .getDataProvider()
            .DataTypeGroup
            .deleteDataTypeGroup(idDataTypeGroup)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, DataTypeGroupService.SIMAX_MODULE_NAME))
            })
    }

    //#endregion

    //#region Details

    public openDetails(idDataTypeGroup: number, pageItemsIds: number[]): JQueryPromise<DetailsResult> {
        const detailsOptions: DetailsOptions = this.getDetailsOptions(idDataTypeGroup);

        detailsOptions.pageItemsIds = pageItemsIds;

        return this.systemService.openDetails(detailsOptions);
    }

    public getDetailsOptions(idDataTypeGroup: number): DetailsOptions {
        return {
            hasPermission: true,
            idReferenceType: Enums.ReferenceType.IdDataTypeGroup,
            moduleName: DataTypeGroupService.SIMAX_MODULE_NAME,
            pageItemsIds: null,
            referenceObjectId: idDataTypeGroup,
        }
    }

    //#endregion

    // #region Parameters

    public getBasicParametersDataTypeGroup(group: OpDataTypeGroup): JQueryPromise<OpDataTypeInGroup[]> {
        return DataTypeGroupComponent.getDataTypeInGroupByModule(
            this.userSession.getDataProvider(),
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
            DataTypeGroupService.SIMAX_MODULE_NAME,
            Enums.ReferenceType.IdDistributor,
            this.userSession.getLoggedOperator().idDistributor
        )
    }

    //#endregion

    // #endregion    

    // #region CreateEdit

    public openCreate(): void {
        const createEditOptions: CreateEditInitParams = this.getCreateEditOptions(
            0,
            undefined,
            undefined,
            this.userSession.getLoggedOperator().idDistributor
        )
        this.systemService.openCreateEdit(createEditOptions)
    }

    public openEdit(idDataTypeGroup: number): void {
        const createEditOptions: CreateEditInitParams = this.getCreateEditOptions(idDataTypeGroup, undefined, Enums.SimaxCreateEditSelectedTabType.BaseParameters, this.userSession.getLoggedOperator().idDistributor)
        this.systemService.openCreateEdit(createEditOptions)
    }

    public getCreateEditOptions(
        referenceObjectId: number = 0,
        referenceObject: OpDataTypeGroup = null,
        selectedTabType: Enums.SimaxCreateEditSelectedTabType = Enums.SimaxCreateEditSelectedTabType.BaseParameters,
        dataTypeGroupReferenceValue: number = null
    ): CreateEditInitParams {
        return {
            createEditSelectedTabType: selectedTabType,
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: dataTypeGroupReferenceValue != null ? dataTypeGroupReferenceValue : null,
            idReferenceType: DataTypeGroupService.REFERENCE_TYPE,
            moduleName: DataTypeGroupService.SIMAX_MODULE_NAME,
            referenceObject: referenceObject,
            referenceObjectId: referenceObjectId || 0,
            sections: null,
            useBusinessObjects: true,
            isCopy: false,
            dedicatedForm: (
                this.userSession.getLoggedOperator().profile != null &&
                this.userSession.getLoggedOperator().profile.parameters != null)
                ? this.userSession.getLoggedOperator().profile.parameters.getValue(Enums.DataType.PROFILE_DATA_TYPE_GROUP_EDIT_FORM)
                : null
        }
    }

    //#endregion

    public getDataTypesInGroupGridColumns(allowEditing?: boolean): JQueryPromise<ColumnModel[]> {
        const deferred: JQueryDeferred<ColumnModel[]> = $.Deferred<ColumnModel[]>()

        const columns: ColumnModel[] = []
        columns.push(<any>{ id: this.idDataTypeColumnName, name: this.idDataTypeColumnName, caption: 'id'/*LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Id)*/, type: Enums.DataTypeClass.Integer, allowEditing: false })
        columns.push(<any>{ id: this.nameDataTypeColumnName, name: this.nameDataTypeColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name), allowEditing: false })
        columns.push(<any>{ id: this.descrDataTypeColumnName, name: this.descrDataTypeColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description), allowEditing: false })

        const orderNbrColumn: ColumnModel = <any>{
            id: this.orderNbrColumnName,
            name: this.orderNbrColumnName,
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Index),
            type: Enums.DataTypeClass.Integer
        }

        if (allowEditing == true) {
            const numberBoxOptions: DevExpress.ui.dxNumberBoxOptions = {}
            numberBoxOptions.showClearButton = true
            numberBoxOptions.showSpinButtons = true
            numberBoxOptions.min = 0

            orderNbrColumn.editorOptions = numberBoxOptions
            orderNbrColumn.allowEditing = allowEditing
        }

        columns.push(orderNbrColumn)
        columns.push(<any>{ id: this.customDescrColumnName, name: this.customDescrColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description), allowEditing: false })

        deferred.resolve(columns)

        return deferred.promise()
    }

    public getDataTypesGridColumns(): JQueryPromise<ColumnModel[]> {
        const deferred: JQueryDeferred<ColumnModel[]> = $.Deferred<ColumnModel[]>()

        const columns: ColumnModel[] = []
        columns.push(<any>{ id: this.idDataTypeColumnName, name: this.idDataTypeColumnName, caption: 'id'/*LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Id)*/, type: Enums.DataTypeClass.Integer })
        columns.push(<any>{ id: this.nameDataTypeColumnName, name: this.nameDataTypeColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name) })
        columns.push(<any>{ id: this.descrDataTypeColumnName, name: this.descrDataTypeColumnName, caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description) })

        deferred.resolve(columns)

        return deferred.promise()
    }

    // #region Private Methods

    private prepareCustomWhereClause(actionView: OpView, columns: ColumnModel[]): string {

        let where: string = TypeHelper.emptyString
        let whereClause: string = TypeHelper.emptyString

        if (actionView != null) {
            if (columns.length == 0) {
                columns = actionView.columns.map((c: OpViewColumn) => {
                    const column: ColumnModel = new ColumnModel()
                    column.id = c.idDataType
                    column.name = c.name
                    return column
                })
            }

            const columnParams: GetViewColumnOptions = new GetViewColumnOptions(DataType.HELPER_CREATION_DATE)
            const column: OpViewColumn = actionView.getViewColumn(columnParams)
        }

        where += whereClause

        if (this.$state.params['ids'] && this.$state.params['ids'].length > 0) {
            const primaryColumn: OpViewColumn = actionView.getPrimaryKeyColumn()

            if (primaryColumn != null) {
                const fColumns: ColumnModel[] = columns.filter((c: ColumnModel) => {
                    if (primaryColumn.idDataType === c.id) {
                        return true
                    }
                    return false
                })

                let where: string = '[' + primaryColumn.name + '] in (' + this.$state.params['ids'].join(',') + ')'

                if (fColumns.length > 0) {
                    const fColumn: ColumnModel = fColumns[0]

                    if (_.size(fColumn.where) === 0) {
                        fColumn.where = [where]
                    } else {
                        fColumn.where.push(where)
                    }
                } else {
                    const fColumn: ColumnModel = new ColumnModel()
                    fColumn.name = primaryColumn.name
                    fColumn.id = primaryColumn.idDataType
                    fColumn.where = [where]
                }

                if (where.length > 0) {
                    where += " and "
                }

                where += where
            }
        }

        return where
    }

    private prepareGridObjects(objects: OpDataTypeGroup[], skipDataSourceSerialization: boolean = false): any[] {
        return _.map(objects, (dtg: OpDataTypeGroup) => {
            const item: any = this.gridService.gridObjectFromIOpObject(dtg)

            item.rowColor = dtg.dataList.getValue(Enums.DataType.HELPER_ROW_COLOR)

            if (dtg.idParentGroup != null) {
                item[DataTypeGroupService.PARENT_ID_FIELD] = dtg.idParentGroup
            } else {
                item[DataTypeGroupService.PARENT_ID_FIELD] = 0
            }

            this.attachSourceObject(dtg, item, skipDataSourceSerialization)

            return item
        })
    }

    /**
     * Attaches the source business object onto the given grid object.
     */
    private attachSourceObject(sourceObject: OpDataTypeGroup, gridObject: any, skipDataSourceSerialization: boolean): void {
        if (skipDataSourceSerialization) {
            gridObject[GridService.SOURCE_OBJECT_FIELD_NAME] = sourceObject
        }
        else if (TypeHelper.isIJSONSerializable(sourceObject)) {
            gridObject[GridService.SOURCE_OBJECT_FIELD_NAME] = (sourceObject as any).toJSON()
        }
        else {
            //console.warn(this.LOG_STRING, 'The given source object is not IJSONSerializable. Unable to attach it to grid object.');

            gridObject[GridService.SOURCE_OBJECT_FIELD_NAME] = null
        }
    }

    // #endregion
}

export class DataTypeGroupServiceProvider
    implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        USER_SESSION_SERVICE_NAME,
        GRID_SERVICE_NAME,
        $STATE_SERVICE_NAME,
        SYSTEM_SERVICE_NAME,
        function (
            userSession: UserSessionService,
            gridService: GridService,
            $state: StateService,
            systemService: SystemService
        ) {
            return new DataTypeGroupService(userSession, gridService, $state, systemService)
        }]

    //#endregion
} 
